import { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { dashboardApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import api from '../lib/api';
import { Button } from './ui/button';
import { Route, Eye, EyeOff, Clock, RefreshCw, Maximize2 } from 'lucide-react';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const createColorIcon = (color) => new L.DivIcon({
  html: `<div style="background:${color};width:14px;height:14px;border-radius:50%;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.3)"></div>`,
  className: '', iconSize: [20, 20], iconAnchor: [10, 10],
});

const youAreHereIcon = new L.DivIcon({
  html: `<div style="position:relative">
    <div style="width:18px;height:18px;background:#ef4444;border-radius:50%;border:3px solid white;box-shadow:0 0 0 3px rgba(239,68,68,0.3),0 2px 8px rgba(0,0,0,0.3)"></div>
    <div style="position:absolute;top:-2px;left:-2px;width:22px;height:22px;border-radius:50%;border:2px solid #ef4444;animation:pulse-ring 1.5s ease-out infinite;opacity:0"></div>
  </div>
  <style>@keyframes pulse-ring{0%{transform:scale(1);opacity:0.8}100%{transform:scale(2.2);opacity:0}}</style>`,
  className: '', iconSize: [22, 22], iconAnchor: [11, 11],
});

const TRAIL_COLORS = ['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#22c55e', '#ec4899', '#14b8a6', '#6366f1'];
const roleColors = { admin: '#6366f1', manager: '#8b5cf6', salesperson: '#3b82f6' };

function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center[0] && center[1]) map.flyTo(center, zoom, { duration: 1.2 });
  }, [center, zoom, map]);
  return null;
}

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length > 0) {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14, duration: 1.2 });
    }
  }, [bounds, map]);
  return null;
}

export function LiveMap() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [liveTrails, setLiveTrails] = useState({});
  const [historicTrails, setHistoricTrails] = useState({});
  const [myLocation, setMyLocation] = useState(null);
  const [showTrails, setShowTrails] = useState(true);
  const [trailHours, setTrailHours] = useState(8);
  const [loadingTrails, setLoadingTrails] = useState(false);
  const [fitAllBounds, setFitAllBounds] = useState(null);
  const token = localStorage.getItem('token');
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  // Fetch user's own location (browser GPS with IP fallback)
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setMyLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude, source: 'gps' });
          if (user?.id) {
            api.put(`/users/${user.id}/location`, { lat: pos.coords.latitude, lng: pos.coords.longitude }).catch(() => {});
          }
        },
        async () => {
          try {
            const res = await api.get('/my-location');
            setMyLocation({ lat: res.data.lat, lng: res.data.lng, source: res.data.source });
          } catch { setMyLocation({ lat: 19.076, lng: 72.877, source: 'default' }); }
        },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    } else {
      api.get('/my-location').then(res => setMyLocation({ lat: res.data.lat, lng: res.data.lng, source: res.data.source }))
        .catch(() => setMyLocation({ lat: 19.076, lng: 72.877, source: 'default' }));
    }
  }, [user?.id]);

  // Fetch team locations
  const fetchLocations = useCallback(async () => {
    try {
      const res = await dashboardApi.getTeamLocations();
      setMembers((res.data || []).filter(m => m.current_location?.lat));
    } catch { /* silent */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch historical route trails
  const fetchTrails = useCallback(async () => {
    if (!isAdmin) return;
    setLoadingTrails(true);
    try {
      const res = await dashboardApi.getTeamTrails(trailHours);
      setHistoricTrails(res.data.trails || {});
    } catch { /* silent */ }
    setLoadingTrails(false);
  }, [isAdmin, trailHours]);

  // Handle WebSocket messages
  const handleWsMessage = useCallback((msg) => {
    if (msg.type === 'location_update') {
      setMembers(prev => {
        const idx = prev.findIndex(m => m.id === msg.user_id);
        if (idx >= 0) {
          const upd = [...prev];
          upd[idx] = { ...upd[idx], current_location: msg.location, status: 'online', name: msg.name || upd[idx].name };
          return upd;
        }
        return [...prev, { id: msg.user_id, name: msg.name, role: msg.role, status: 'online', current_location: msg.location }];
      });
      setLiveTrails(prev => ({
        ...prev,
        [msg.user_id]: [...(prev[msg.user_id] || []).slice(-50), [msg.location.lat, msg.location.lng]]
      }));
    } else if (msg.type === 'user_status') {
      setMembers(prev => prev.map(m => m.id === msg.user_id ? { ...m, status: msg.status } : m));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useWebSocket(token, handleWsMessage);

  useEffect(() => {
    fetchLocations();
    fetchTrails();
    const i = setInterval(fetchLocations, 30000);
    return () => clearInterval(i);
  }, [fetchLocations, fetchTrails]);

  const onlineCount = members.filter(m => m.status === 'online').length;
  const center = myLocation ? [myLocation.lat, myLocation.lng] : [19.076, 72.877];

  // Merge historic + live trails for display
  const mergedTrails = {};
  if (showTrails && isAdmin) {
    Object.entries(historicTrails).forEach(([uid, data]) => {
      mergedTrails[uid] = { ...data, points: [...data.points] };
    });
    Object.entries(liveTrails).forEach(([uid, pts]) => {
      if (mergedTrails[uid]) {
        mergedTrails[uid].points = [...mergedTrails[uid].points, ...pts];
      } else {
        const member = members.find(m => m.id === uid);
        mergedTrails[uid] = { name: member?.name || 'Unknown', role: member?.role || 'salesperson', points: pts, count: pts.length };
      }
    });
  }

  const trailUserIds = Object.keys(mergedTrails);

  return (
    <div className="space-y-3" data-testid="live-map">
      {/* Header with trail controls */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
          <span className="text-sm text-gray-600">
            {isAdmin ? `${onlineCount} team members online` : 'Your location'}
          </span>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* Trail controls (admin/manager only) */}
          {isAdmin && (
            <>
              <Button
                variant={showTrails ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() => setShowTrails(!showTrails)}
                data-testid="toggle-trails-btn"
              >
                {showTrails ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                Trails
              </Button>
              {showTrails && (
                <select
                  value={trailHours}
                  onChange={(e) => setTrailHours(Number(e.target.value))}
                  className="h-7 text-xs border rounded-md px-2 bg-white"
                  data-testid="trail-hours-select"
                >
                  <option value={2}>2h</option>
                  <option value={4}>4h</option>
                  <option value={8}>8h</option>
                  <option value={24}>24h</option>
                  <option value={48}>48h</option>
                </select>
              )}
              <Button
                variant="ghost" size="sm" className="h-7 w-7 p-0"
                onClick={() => { fetchLocations(); fetchTrails(); }}
                data-testid="refresh-map-btn"
              >
                <RefreshCw className={`w-3 h-3 ${loadingTrails ? 'animate-spin' : ''}`} />
              </Button>
              {members.length > 0 && (
                <Button
                  variant="outline" size="sm" className="h-7 text-xs gap-1"
                  onClick={() => {
                    const allPts = [];
                    if (myLocation) allPts.push([myLocation.lat, myLocation.lng]);
                    members.forEach(m => { if (m.current_location?.lat) allPts.push([m.current_location.lat, m.current_location.lng]); });
                    if (allPts.length > 0) setFitAllBounds(allPts);
                  }}
                  data-testid="fit-team-btn"
                >
                  <Maximize2 className="w-3 h-3" />
                  Fit Team
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 text-xs flex-wrap">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500 border-2 border-white shadow" />You</span>
        {isAdmin && (
          <>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500" />Online</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-gray-400" />Offline</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-blue-500" />Salesperson</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-500" />Manager</span>
            {showTrails && trailUserIds.length > 0 && (
              <span className="flex items-center gap-1 ml-1 pl-1 border-l border-gray-300">
                <Route className="w-3 h-3 text-blue-500" />
                <span className="text-gray-500">{trailUserIds.length} trail{trailUserIds.length !== 1 ? 's' : ''}</span>
              </span>
            )}
          </>
        )}
      </div>

      {/* Map */}
      <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm" style={{ height: '400px' }}>
        <MapContainer center={center} zoom={myLocation ? 12 : 5} style={{ height: '100%', width: '100%' }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
          {myLocation && !fitAllBounds && <FlyTo center={[myLocation.lat, myLocation.lng]} zoom={12} />}
          {fitAllBounds && <FitBounds bounds={fitAllBounds} />}

          {/* "You are here" red pulsing dot */}
          {myLocation && (
            <Marker position={[myLocation.lat, myLocation.lng]} icon={youAreHereIcon} zIndexOffset={1000}>
              <Popup>
                <div className="text-sm min-w-[140px]">
                  <p className="font-bold text-red-600">You are here</p>
                  <p className="font-medium">{user?.name}</p>
                  <p className="text-gray-500 capitalize text-xs">{user?.role}</p>
                  <p className="text-xs text-gray-400 mt-1">{myLocation.lat.toFixed(4)}, {myLocation.lng.toFixed(4)}</p>
                  <p className="text-xs text-gray-400">Source: {myLocation.source === 'gps' ? 'GPS' : myLocation.source === 'ip' ? 'IP Location' : 'Default'}</p>
                </div>
              </Popup>
            </Marker>
          )}

          {/* Team members (admin/manager sees all) */}
          {isAdmin && members.map((member) => {
            const loc = member.current_location;
            if (!loc?.lat || !loc?.lng) return null;
            if (member.id === user?.id) return null;
            const color = member.status === 'online' ? (roleColors[member.role] || '#3b82f6') : '#9ca3af';
            return (
              <Marker key={member.id} position={[loc.lat, loc.lng]} icon={createColorIcon(color)}>
                <Popup>
                  <div className="text-sm min-w-[150px]">
                    <p className="font-semibold">{member.name}</p>
                    <p className="text-gray-500 capitalize">{member.role}</p>
                    <p className="text-xs text-gray-400">{loc.lat.toFixed(4)}, {loc.lng.toFixed(4)}</p>
                    <p className="text-xs mt-1">
                      <span className={`inline-block w-2 h-2 rounded-full mr-1 ${member.status === 'online' ? 'bg-green-500' : 'bg-gray-400'}`} />
                      {member.status}
                    </p>
                    {loc.updated_at && <p className="text-xs text-gray-400 mt-1">Updated: {new Date(loc.updated_at).toLocaleTimeString()}</p>}
                  </div>
                </Popup>
              </Marker>
            );
          })}

          {/* Route trails (historical + live merged) */}
          {showTrails && isAdmin && trailUserIds.map((uid, idx) => {
            const trail = mergedTrails[uid];
            if (!trail || trail.points.length < 2) return null;
            const color = TRAIL_COLORS[idx % TRAIL_COLORS.length];
            return (
              <Polyline
                key={`trail-${uid}`}
                positions={trail.points}
                pathOptions={{
                  color,
                  weight: 3,
                  opacity: 0.7,
                  lineCap: 'round',
                  lineJoin: 'round',
                }}
              >
                <Popup>
                  <div className="text-sm min-w-[130px]">
                    <p className="font-semibold" style={{ color }}>{trail.name}</p>
                    <p className="text-gray-500 capitalize text-xs">{trail.role}</p>
                    <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                      <Clock className="w-3 h-3" />
                      {trail.count} points in last {trailHours}h
                    </div>
                  </div>
                </Popup>
              </Polyline>
            );
          })}
        </MapContainer>
      </div>

      {/* Trail summary (admin only) */}
      {showTrails && isAdmin && trailUserIds.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="trail-summary">
          {trailUserIds.map((uid, idx) => {
            const trail = mergedTrails[uid];
            if (!trail) return null;
            const color = TRAIL_COLORS[idx % TRAIL_COLORS.length];
            return (
              <div key={uid} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-gray-50 border text-xs">
                <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: color }} />
                <span className="text-gray-700 font-medium">{trail.name}</span>
                <span className="text-gray-400">{trail.count} pts</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
